import { type ComponentType, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Sparkles,
  RefreshCw,
  Info,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  MinusCircle,
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import type { UIChecklistItem } from "@/packages/types";
import { AIGeneratePopover } from "./AIGeneratePopover";

// ─── Skeleton Loader ──────────────────────────────────────────────────────────

function SkeletonLoader({ label }: { label: string }) {
  return (
    <div className="py-5 space-y-4">
      <div className="flex items-center gap-2.5">
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-accent"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                delay: i * 0.2,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
        <span className="text-[12px] text-accent font-medium tracking-wide">
          {label}
        </span>
      </div>
      <div className="space-y-2.5">
        {[90, 72, 85, 55].map((w) => (
          <div
            key={w}
            className="h-2.5 rounded-full shimmer"
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Summary Report Types ─────────────────────────────────────────────────────

interface ChecklistSummaryReport {
  complianceStatus: string;
  confidence: number;
  reasoning: string;
  missingElements?: string[];
  recommendation?: string;
}

function parseSummaryReport(
  raw: string,
): ChecklistSummaryReport | string | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "complianceStatus" in parsed &&
      "confidence" in parsed &&
      "reasoning" in parsed
    ) {
      return parsed as ChecklistSummaryReport;
    }
    return raw;
  } catch {
    return raw;
  }
}

// ─── Compliance Status Config ─────────────────────────────────────────────────

type ComplianceLevel = "compliant" | "partial" | "missing" | "non-compliant";

function getComplianceLevel(status: string): ComplianceLevel {
  const s = status.toLowerCase();
  if (s.includes("non") || s.includes("fail")) return "non-compliant";
  if (s.includes("partial")) return "partial";
  if (s.includes("missing")) return "missing";
  if (s.includes("compliant") || s.includes("pass")) return "compliant";
  return "missing";
}

const COMPLIANCE_CONFIG: Record<
  ComplianceLevel,
  {
    icon: ComponentType<{ size?: number; className?: string }>;
    bannerBg: string;
    bannerBorder: string;
    iconColor: string;
    textColor: string;
    barColor: string;
  }
> = {
  compliant: {
    icon: CheckCircle2,
    bannerBg: "bg-green-500/8",
    bannerBorder: "border-green-500/20",
    iconColor: "text-green-600",
    textColor: "text-green-700",
    barColor: "bg-green-500",
  },
  partial: {
    icon: MinusCircle,
    bannerBg: "bg-orange-500/8",
    bannerBorder: "border-orange-500/20",
    iconColor: "text-orange-600",
    textColor: "text-orange-700",
    barColor: "bg-orange-500",
  },
  missing: {
    icon: AlertTriangle,
    bannerBg: "bg-amber-500/8",
    bannerBorder: "border-amber-500/20",
    iconColor: "text-amber-600",
    textColor: "text-amber-700",
    barColor: "bg-amber-500",
  },
  "non-compliant": {
    icon: XCircle,
    bannerBg: "bg-red-500/8",
    bannerBorder: "border-red-500/20",
    iconColor: "text-red-600",
    textColor: "text-red-700",
    barColor: "bg-red-500",
  },
};

// ─── Structured Summary Card ──────────────────────────────────────────────────

function StructuredSummaryCard({ report }: { report: ChecklistSummaryReport }) {
  const { t } = useTranslation("audits");
  const [reasoningExpanded, setReasoningExpanded] = useState(false);

  const level = getComplianceLevel(report.complianceStatus);
  const config = COMPLIANCE_CONFIG[level];
  const StatusIcon = config.icon;
  const pct = Math.round(report.confidence * 100);
  const missing = report.missingElements ?? [];

  return (
    <motion.div
      className="space-y-3"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Compliance status banner */}
      <div
        className={`rounded-xl border px-4 py-3.5 ${config.bannerBg} ${config.bannerBorder}`}
      >
        <div className="flex items-center gap-3">
          <StatusIcon size={22} className={config.iconColor} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted font-medium mb-0.5">
              {t("checklist.ai.confidence").toUpperCase()} STATUS
            </p>
            <p
              className={`text-[15px] font-semibold leading-tight ${config.textColor}`}
            >
              {report.complianceStatus}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p
              className={`text-[22px] font-bold leading-none ${config.textColor}`}
            >
              {pct}%
            </p>
            <p className="text-[10px] text-muted mt-0.5">
              {t("checklist.ai.confidence")}
            </p>
          </div>
        </div>

        {/* Confidence bar */}
        <div className="mt-3">
          <div className="h-1.5 w-full rounded-full bg-black/8 overflow-hidden">
            <motion.div
              className={`h-full rounded-full ${config.barColor}`}
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
            />
          </div>
        </div>
      </div>

      {/* Reasoning */}
      <div className="rounded-xl border border-border bg-page/60 overflow-hidden">
        <div className="px-4 pt-3 pb-1">
          <p className="text-[10px] uppercase tracking-widest text-muted font-medium mb-2">
            {t("checklist.ai.reasoning")}
          </p>
          <div className="border-l-2 border-accent/50 pl-3">
            <p
              className={`text-[13px] text-secondary leading-relaxed ${!reasoningExpanded ? "line-clamp-4" : ""}`}
            >
              {report.reasoning}
            </p>
          </div>
        </div>
        {report.reasoning.length > 200 && (
          <button
            type="button"
            onClick={() => setReasoningExpanded((v) => !v)}
            className="flex items-center gap-1 w-full px-4 py-2.5 text-[11px] text-accent hover:text-accent/80 font-medium border-t border-border/50 hover:bg-accent-soft/30 transition-colors"
          >
            <motion.span
              animate={{ rotate: reasoningExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="inline-flex"
            >
              <ChevronDown size={12} />
            </motion.span>
            {reasoningExpanded
              ? t("checklist.ai.showLess")
              : t("checklist.ai.showMore")}
          </button>
        )}
      </div>

      {/* Missing Elements */}
      {missing.length > 0 && (
        <div className="rounded-xl border border-border bg-page/60 px-4 py-3.5 space-y-2.5">
          <p className="text-[10px] uppercase tracking-widest text-muted font-medium">
            {t("checklist.ai.missingElements")}
          </p>
          <ul className="space-y-2">
            {missing.map((el, idx) => (
              <li key={el} className="flex items-start gap-3">
                <span className="text-[10px] font-bold text-muted/60 pt-0.5 tabular-nums shrink-0 w-4">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className="text-[13px] text-secondary leading-snug">
                  {el}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendation */}
      {report.recommendation && (
        <div className="flex items-center gap-3 rounded-xl border border-accent/20 bg-accent-soft px-4 py-3">
          <ArrowRight size={14} className="text-accent shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted font-medium mb-0.5">
              {t("checklist.ai.recommendation")}
            </p>
            <p className="text-[13px] font-semibold text-accent leading-snug">
              {report.recommendation}
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ─── Summary Section ──────────────────────────────────────────────────────────

export function AISummarySection({
  item,
  onGenerateSummary,
}: {
  item: UIChecklistItem;
  onGenerateSummary?: () => void;
}) {
  const { t } = useTranslation("audits");

  if (item.summaryStatus === "pending") {
    return <SkeletonLoader label={t("checklist.ai.generatingSummary")} />;
  }

  if (!item.summaryReport) {
    return (
      <div className="flex flex-col items-center text-center py-8 px-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent-soft mb-3.5">
          <Sparkles size={20} className="text-accent" />
        </div>
        <p className="text-[13px] text-secondary leading-relaxed mb-5 max-w-[240px]">
          {t("checklist.ai.generateSummaryDescription")}
        </p>
        <Button
          variant="primary"
          size="sm"
          icon={Sparkles}
          onClick={onGenerateSummary}
          disabled={!onGenerateSummary}
          className="border border-green-500/60 hover:border-green-500"
        >
          {t("checklist.ai.generateSummary")}
        </Button>
      </div>
    );
  }

  const parsed = parseSummaryReport(item.summaryReport);

  if (typeof parsed === "string" || parsed === null) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-border bg-page/60 px-4 py-3.5">
          <div className="border-l-2 border-accent/50 pl-3">
            <p className="text-[13px] text-secondary leading-relaxed">
              {item.summaryReport}
            </p>
          </div>
        </div>
        {onGenerateSummary && (
          <Button
            variant="accent-soft"
            size="sm"
            icon={RefreshCw}
            onClick={onGenerateSummary}
            className="border border-green-500/60 hover:border-green-500"
          >
            {t("checklist.ai.regenerate")}
          </Button>
        )}
      </div>
    );
  }

  return <StructuredSummaryCard report={parsed} />;
}

// ─── Information Content ──────────────────────────────────────────────────────

export function AIInformationContent({
  item,
  onGenerateInformation,
}: {
  item: UIChecklistItem;
  onGenerateInformation?: (instructions: string) => void;
}) {
  const { t } = useTranslation("audits");
  const infoPending = item.informationStatus === "pending";

  return (
    <AnimatePresence mode="wait">
      {infoPending ? (
        <motion.div
          key="info-pending"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <SkeletonLoader label={t("checklist.ai.generatingInformation")} />
        </motion.div>
      ) : item.informationReport ? (
        <motion.div
          key="info-content"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="space-y-2"
        >
          {onGenerateInformation && (
            <div className="flex justify-end">
              <AIGeneratePopover
                onGenerate={onGenerateInformation}
                label={t("checklist.ai.regenerate")}
              >
                <Button
                  variant="accent-soft"
                  size="sm"
                  icon={RefreshCw}
                  className="border border-accent/30 hover:border-accent/60"
                >
                  {t("checklist.ai.regenerate")}
                </Button>
              </AIGeneratePopover>
            </div>
          )}
          <div className="rounded-xl border border-border bg-page/60 px-4 py-3.5">
            <div className="prose-chat text-[13px] text-secondary leading-relaxed">
              <Markdown remarkPlugins={[remarkGfm]}>
                {item.informationReport}
              </Markdown>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="info-empty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-xl border border-dashed border-border/60 px-5 py-8 flex flex-col items-center text-center gap-3"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft">
            <Info size={16} className="text-accent" />
          </div>
          <div className="space-y-1">
            <p className="text-[13px] font-medium text-secondary">
              {t("checklist.ai.noInformation")}
            </p>
            <p className="text-[12px] text-muted leading-relaxed max-w-[260px]">
              {t("checklist.ai.noInformationDescription")}
            </p>
          </div>
          {onGenerateInformation && (
            <AIGeneratePopover
              onGenerate={onGenerateInformation}
              label={t("checklist.ai.generateInformation")}
            >
              <Button
                variant="accent-soft"
                size="sm"
                icon={Sparkles}
                className="border border-accent/30 hover:border-accent/60"
              >
                {t("checklist.ai.generateInformation")}
              </Button>
            </AIGeneratePopover>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Examples Content ─────────────────────────────────────────────────────────

export function AIExamplesContent({
  item,
  onGenerateExamples,
}: {
  item: UIChecklistItem;
  onGenerateExamples?: (instructions: string) => void;
}) {
  const { t } = useTranslation("audits");
  const examples = item.exampleDocuments ?? [];
  const examplesPending = item.exampleDocumentsStatus === "pending";

  return (
    <AnimatePresence mode="wait">
      {examplesPending ? (
        <motion.div
          key="ex-pending"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <SkeletonLoader label={t("checklist.ai.generatingExamples")} />
        </motion.div>
      ) : examples.length > 0 ? (
        <motion.div
          key="ex-content"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="space-y-2"
        >
          {onGenerateExamples && (
            <div className="flex justify-end">
              <AIGeneratePopover
                onGenerate={onGenerateExamples}
                label={t("checklist.ai.regenerate")}
              >
                <Button
                  variant="accent-soft"
                  size="sm"
                  icon={RefreshCw}
                  className="border border-accent/30 hover:border-accent/60"
                >
                  {t("checklist.ai.regenerate")}
                </Button>
              </AIGeneratePopover>
            </div>
          )}
          <div className="space-y-2.5">
            {examples.map((example, idx) => (
              <div
                key={example.name}
                className="rounded-xl border border-border bg-page/60 overflow-hidden"
              >
                <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-border/50 bg-card">
                  <span className="text-[10px] font-bold text-muted/50 tabular-nums">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <p className="text-[12px] font-semibold text-primary flex-1 min-w-0 truncate">
                    {example.name}
                  </p>
                </div>
                <div className="px-4 py-3">
                  <div className="prose-chat text-[13px] text-secondary leading-relaxed">
                    <Markdown remarkPlugins={[remarkGfm]}>
                      {example.content}
                    </Markdown>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="ex-empty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-xl border border-dashed border-border/60 px-5 py-8 flex flex-col items-center text-center gap-3"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft">
            <Sparkles size={16} className="text-accent" />
          </div>
          <div className="space-y-1">
            <p className="text-[13px] font-medium text-secondary">
              {t("checklist.ai.noExamples")}
            </p>
            <p className="text-[12px] text-muted leading-relaxed max-w-[260px]">
              {t("checklist.ai.noExamplesDescription")}
            </p>
          </div>
          {onGenerateExamples && (
            <AIGeneratePopover
              onGenerate={onGenerateExamples}
              label={t("checklist.ai.examples")}
            >
              <Button
                variant="accent-soft"
                size="sm"
                icon={Sparkles}
                className="border border-accent/30 hover:border-accent/60"
              >
                {t("checklist.ai.examples")}
              </Button>
            </AIGeneratePopover>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
